import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, KeyRound, Trash2 } from 'lucide-react';
import { apiKeysApi, mcpEndpoint, MintedKey } from '@/lib/apiKeysApi';
import { useToast, useConfirm } from '@/components/ui';

/**
 * Settings → MCP server (W3). Lets a workspace mint API keys so an external AI
 * agent (Claude, etc.) can connect over the Model Context Protocol with
 * read-only, workspace-scoped access. The minted key is shown exactly once.
 */
export function McpKeysSection({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState('');
  const [minted, setMinted] = useState<MintedKey | null>(null);

  const endpoint = mcpEndpoint();

  const list = useQuery({
    queryKey: ['api-keys', wsId],
    queryFn: () => apiKeysApi.list(wsId),
    enabled: !!wsId,
  });

  const mint = useMutation({
    mutationFn: () => apiKeysApi.mint(wsId, name.trim()),
    onSuccess: (data) => {
      setMinted(data);
      setName('');
      qc.invalidateQueries({ queryKey: ['api-keys', wsId] });
    },
    onError: () => toast.error(t('settings.mcp_mint_failed')),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(wsId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys', wsId] });
      toast.success(t('settings.mcp_revoked'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('settings.mcp_copied'));
    } catch {
      /* clipboard blocked */
    }
  }

  async function askRevoke(id: string) {
    const ok = await confirm({
      title: t('settings.mcp_revoke_confirm_title'),
      description: t('settings.mcp_revoke_confirm_body'),
      confirmLabel: t('settings.mcp_revoke'),
      variant: 'danger',
    });
    if (ok) revoke.mutate(id);
  }

  const rows = list.data ?? [];

  return (
    <section className="rounded-2xl bg-white border border-ink-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4 text-ink-500" />
        <h2 className="text-sm font-semibold text-ink-900">{t('settings.mcp_section')}</h2>
      </div>
      <p className="text-xs text-ink-500 mb-3">{t('settings.mcp_intro')}</p>

      <div className="mb-4 rounded-md border border-ink-200 bg-ink-50 px-3 py-2">
        <p className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-semibold">
          {t('settings.mcp_endpoint_label')}
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate text-xs font-mono text-ink-900">{endpoint}</code>
          <button
            onClick={() => copy(endpoint)}
            className="shrink-0 rounded p-1 text-ink-400 hover:text-ink-700"
            aria-label={t('settings.mcp_copy')}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {minted && (
        <div className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-3">
          <p className="text-[13px] font-semibold text-emerald-900">{t('settings.mcp_minted_title')}</p>
          <p className="text-xs text-emerald-700 mb-2">{t('settings.mcp_minted_note')}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-xs font-mono text-emerald-900 bg-white rounded px-2 py-1 border border-emerald-200">
              {minted.key}
            </code>
            <button
              onClick={() => copy(minted.key)}
              className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              <Copy className="h-3.5 w-3.5" />
              {t('settings.mcp_copy')}
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && !mint.isPending) mint.mutate();
        }}
        className="flex items-center gap-2 mb-4"
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('settings.mcp_new_key_placeholder')}
          maxLength={100}
          className="flex-1 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <button
          type="submit"
          disabled={!name.trim() || mint.isPending}
          className="shrink-0 rounded-md bg-ink-900 px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
        >
          {t('settings.mcp_mint')}
        </button>
      </form>

      {list.isLoading ? (
        <p className="text-xs text-ink-500">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-500">{t('settings.mcp_empty')}</p>
      ) : (
        <ul className="divide-y divide-ink-100 rounded-md border border-ink-200">
          {rows.map((k) => (
            <li key={k.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink-900 truncate">{k.name}</p>
                <p className="text-xs text-ink-500 font-mono">
                  {k.prefix}…·{' '}
                  {t('settings.mcp_last_used')}:{' '}
                  {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : t('settings.mcp_never')}
                </p>
              </div>
              <button
                onClick={() => askRevoke(k.id)}
                disabled={revoke.isPending}
                aria-label={t('settings.mcp_revoke')}
                title={t('settings.mcp_revoke')}
                className="shrink-0 rounded-md p-1.5 text-ink-400 hover:bg-warn-500/10 hover:text-warn-600 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
